import React, { useState, useEffect, useCallback } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const MoversReadingPart6 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;
    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) { allAnswers = {}; }
    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => { answersToSave[qId] = { answer: currentAnswers[qId] }; });
    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => { saveAnswersToLocalStorage(answers); }, 500);
    return () => { clearTimeout(debounceSave); saveAnswersToLocalStorage(answers); };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const { image_url } = exerciseData.content_data;
  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';
  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${ASSET_BASE_URL}${url}`;
  };

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);

  // Group into sections: examples, then sections marked by paragraph_question in options
  const examples = sortedQuestions.filter(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);

  // Build sections from main questions
  const sections = [];
  for (const q of mainQuestions) {
    const opt = q.options && q.options[0];
    const sectionHeader = opt && typeof opt === 'object' && opt.paragraph_question;
    if (sectionHeader) {
      sections.push({ header: sectionHeader, questions: [q] });
    } else if (sections.length > 0) {
      sections[sections.length - 1].questions.push(q);
    } else {
      sections.push({ header: null, questions: [q] });
    }
  }

  const handleChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  let questionCounter = 0;

  const renderQuestion = (q, isExample = false) => {
    if (!isExample) questionCounter++;
    const rawText = q.question_text.replace(/\(Example\)/gi, '').trim();
    const parts = rawText.split('{{big_gap}}');
    const before = parts[0] || '';
    const after = parts.length > 1 ? parts[1] : '';
    const isFullSentence = !before.trim() || rawText === '{{big_gap}}';

    const isAnswerCorrect = q.is_correct === true || q.is_correct === 'true';
    const showIncorrect = isReviewMode && !isExample && !isAnswerCorrect;

    const displayValue = isExample
      ? (q.accepted_answers && q.accepted_answers[0]) || ''
      : (showHint || (isReviewMode && isAnswerCorrect))
        ? q.accepted_answers[0]
        : answers[q.id] || '';

    return (
      <div key={q.id} className={`p-3 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100' : 'bg-white border-slate-100 shadow-sm'}`}>
        <div className="flex items-start gap-3">
          <span className={`shrink-0 font-black px-2.5 py-1 rounded-xl text-sm border shadow-sm mt-0.5 ${isExample ? 'text-white bg-amber-500 border-amber-500' : 'text-slate-400 bg-slate-50 border-slate-100'}`}>
            {isExample ? 'e.g.' : questionCounter}
          </span>
          <div className="flex-1 min-w-0">
            {isFullSentence ? (
              /* Full sentence input (write two sentences) */
              <div>
                {showIncorrect ? (
                  <div className="bg-red-50 px-3 py-2 rounded-xl border border-red-100">
                    {q.user_answer?.answer
                      ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold text-sm">{q.user_answer.answer}</span>
                      : <span className="text-gray-400 mr-2 font-bold">__</span>
                    }
                    <span className="text-green-600 font-bold text-sm">{q.accepted_answers[0]}</span>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={displayValue}
                    onChange={isExample ? undefined : (e) => handleChange(q.id, e.target.value)}
                    readOnly={isExample || showHint || !isHydrated}
                    disabled={isReviewMode || !isHydrated}
                    className={`exercise-input-field exercise-input-text w-full py-2 px-3 font-bold text-sm !rounded-xl ${isExample ? 'exercise-example-text' : ''} ${showHint && !isExample ? 'hint-input' : ''}`}
                    autoComplete="off"
                    placeholder="Write a sentence..."
                  />
                )}
              </div>
            ) : (
              /* Gap fill */
              <div>
                <p className="font-bold text-slate-700 text-sm leading-relaxed m-0 inline">{before}</p>
                {showIncorrect ? (
                  <span className="inline-flex items-center bg-red-50 px-2 py-0.5 rounded-lg border border-red-100 mx-1 align-middle">
                    {q.user_answer?.answer
                      ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold text-sm">{q.user_answer.answer}</span>
                      : <span className="text-gray-400 mr-2 font-bold">__</span>
                    }
                    <span className="text-green-600 font-bold text-sm">{q.accepted_answers[0]}</span>
                  </span>
                ) : (
                  <input
                    type="text"
                    value={displayValue}
                    onChange={isExample ? undefined : (e) => handleChange(q.id, e.target.value)}
                    readOnly={isExample || showHint || !isHydrated}
                    disabled={isReviewMode || !isHydrated}
                    className={`exercise-input-field exercise-input-text !w-[200px] text-center mx-1 align-middle text-sm px-1 py-1 font-bold !rounded-xl ${isExample ? 'exercise-example-text' : ''} ${showHint && !isExample ? 'hint-input' : ''}`}
                    autoComplete="off"
                  />
                )}
                {after && <p className="font-bold text-slate-700 text-sm leading-relaxed m-0 inline">{after}</p>}
              </div>
            )}
            {isReviewMode && !isExample && (
              <span className="inline-flex items-center gap-1 mt-1">
                {isAnswerCorrect
                  ? <span className="correct-checkmark scale-75">✓</span>
                  : <span className="incorrect-checkmark scale-75">✗</span>
                }
                <QuestionLearnButton question={q} size={18} />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-6">
        {/* Image */}
        {image_url && (
          <div className="flex justify-center">
            <img
              src={resolveUrl(image_url)}
              alt="Exercise illustration"
              className="rounded-2xl max-w-full max-h-72 object-contain shadow-sm border border-gray-100 p-1 bg-white"
            />
          </div>
        )}

        {/* Examples */}
        {examples.length > 0 && (
          <div className="space-y-2">
            <h3 className="font-display font-bold text-sm text-slate-500 uppercase tracking-wider">Examples</h3>
            {examples.map(q => renderQuestion(q, true))}
          </div>
        )}

        {/* Sections */}
        {sections.map((section, sIdx) => (
          <div key={sIdx} className="space-y-2">
            {section.header && (
              <h3 className="font-display font-bold text-base text-slate-800 flex items-center gap-2 pt-2">
                <span className="w-7 h-7 rounded-full bg-hupath-scout/10 flex items-center justify-center text-hupath-scout shrink-0">
                  <span className="material-symbols-outlined text-sm">edit</span>
                </span>
                {section.header}
              </h3>
            )}
            {section.questions.map(q => renderQuestion(q, false))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MoversReadingPart6;
